import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { zim } from '../../../zegoConfig';
import { loginToZego } from '../../../components/utils/zegoLogin';
import axios from '../../../api/axios';
import { sendMessageToReactNativeApp } from "../../../components/utils/sendMessageToReactNativeApp";
import { ZIM as ZimType, ZIMMessage } from 'zego-zim-web';
import { decryptFromBackend } from '../../../utils/urlDecryptor';

interface TeleconsultationData {
  patient_id: string;
  patient_name: string;
  practitioner_id: string;
  teleconsultation_start_time: string | null;
  teleconsultation_created_at: string | null; // When the consultation was requested
  server_time: string; // Current server time for accurate calculation
}

interface UserData {
  id: string;
  person: {
    name: string;
  };
  practitioner_role_id: string;
}

const ConnectingDoctor: React.FC = () => {
  const navigate = useNavigate();
  const { teleconsultationId, token } = useParams<{
    teleconsultationId: string;
    token: string;
  }>();

  const decryptedToken = useMemo(() => {
    if (!token) return '';
    try {
      return decryptFromBackend(token);
    } catch (err) {
      console.error('Failed to decrypt token:', err);
      return '';
    }
  }, [token]);

  // States
  const [userData, setUserData] = useState<UserData | null>(null);
  const [teleconsultationData, setTeleconsultationData] = useState<TeleconsultationData | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zimLoginFailed, setZimLoginFailed] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const timerStartTimeRef = useRef<number | null>(null);
  const serverTimeRef = useRef<number | null>(null); // Store server-provided time
  const WAITING_TIME = 180;

  // Early return if token decryption failed
  const isTokenValid = useMemo(() => {
    return decryptedToken !== '';
  }, [decryptedToken]);

  // Calculate remaining time from teleconsultation data (server timestamps)
  useEffect(() => {
    if (!teleconsultationData) return;

    const { teleconsultation_created_at, server_time } = teleconsultationData;

    // If no creation time, use default
    if (!teleconsultation_created_at || !server_time) {
      console.log('⏱️ No server timestamps, using default 180s');
      serverTimeRef.current = WAITING_TIME;
      timerStartTimeRef.current = Date.now();
      setTimeLeft(WAITING_TIME);
      return;
    }

    // Calculate remaining time based on server timestamps
    const createdAt = new Date(teleconsultation_created_at).getTime();
    const serverNow = new Date(server_time).getTime();
    const elapsedSeconds = Math.floor((serverNow - createdAt) / 1000);
    const remainingTime = Math.max(0, WAITING_TIME - elapsedSeconds);

    console.log(`⏱️ Server: created_at=${teleconsultation_created_at}, server_time=${server_time}`);
    console.log(`⏱️ Elapsed: ${elapsedSeconds}s, Remaining: ${remainingTime}s`);

    serverTimeRef.current = remainingTime;
    timerStartTimeRef.current = Date.now();
    setTimeLeft(remainingTime);

    // If already expired, show timeout immediately
    if (remainingTime <= 0) {
      setIsTimeout(true);
    }
  }, [teleconsultationData]);

  // Countdown timer - uses elapsed time since API response, not device clock
  useEffect(() => {
    if (timeLeft === null || timerStartTimeRef.current === null || serverTimeRef.current === null) return;

    if (timeLeft <= 0) {
      console.log("⏰ Timer habis");
      setIsTimeout(true);
      return;
    }

    const timer = setInterval(() => {
      if (timerStartTimeRef.current === null || serverTimeRef.current === null) return;

      // Calculate elapsed time since we received the server response
      const elapsedSeconds = Math.floor((Date.now() - timerStartTimeRef.current) / 1000);
      const remaining = Math.max(0, serverTimeRef.current - elapsedSeconds);

      setTimeLeft(remaining);

      if (remaining <= 0) {
        setIsTimeout(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, teleconsultationId]);

  // Refs
  const previousUserDokterRef = useRef<string | null>(null);
  const isCancelledRef = useRef(false);
  const isLoadingRef = useRef(false); // Guard against duplicate API calls (React StrictMode)

  // Utility functions
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const handleNavigationError = useCallback((error: any) => {
    if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      navigate('/not-found');
    } else if (error.status === 403) {
      console.log('You Are Not Authorized to access this resource!')
      navigate('/forbidden');
    } else {
      console.error('Unexpected error:', error);
    }
  }, [navigate]);

  // API calls
  const fetchUserData = useCallback(async (): Promise<UserData | null> => {
    if (!decryptedToken) {
      setError('Token tidak valid. Silakan coba lagi.');
      return null;
    }

    try {
      const response = await axios.post('users', null, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const data = response.data.data;
      localStorage.setItem('userData', JSON.stringify(data));
      console.log('✅ User data berhasil di-fetch:', data);
      return data;
    } catch (error: any) {
      console.error('❌ Fetch user data gagal:', error);
      if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
        navigate('/not-found');
      } else if (isAxiosError(error) && error.response?.status === 403) {
        navigate('/forbidden');
      } else {
        setError('Gagal memuat data pengguna. Silakan periksa koneksi internet Anda.');
      }
      return null;
    }
  }, [decryptedToken, navigate]);

  const fetchTeleconsultationData = useCallback(async (): Promise<TeleconsultationData | null> => {
    console.log('fetchTeleconsultationData');
    if (!decryptedToken || !teleconsultationId) {
      setError('Data konsultasi tidak lengkap.');
      return null;
    }

    try {
      const response = await axios.get(`teleconsultation/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const data = response.data;
      localStorage.setItem('teleconsultationData', JSON.stringify(data));
      console.log('✅ Teleconsultation data berhasil di-fetch:', data);
      return data;
    } catch (error: any) {
      console.error('❌ Teleconsultation data gagal di-fetch:', error);
      if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
        navigate('/not-found');
      } else if (isAxiosError(error) && error.response?.status === 403) {
        navigate('/forbidden');
      } else {
        setError('Gagal memuat data konsultasi. Silakan periksa koneksi internet Anda.');
      }
      return null;
    }
  }, [decryptedToken, teleconsultationId, navigate]);

  // Validation functions
  const isValidUserData = (data: any): data is UserData => {
    return data &&
      data.id &&
      data.person?.name &&
      data.practitioner_role_id;
  };

  const isValidTeleconsultationData = (data: any): data is TeleconsultationData => {
    return data &&
      data.patient_id &&
      data.patient_name &&
      data.practitioner_id;
  };

  // Verify with API before redirecting to chat
  const verifyAndRedirect = useCallback(async () => {
    if (!teleconsultationId || !decryptedToken) return false;

    console.log('🔍 Verifying teleconsultation status before redirect...');
    try {
      const response = await axios.get(`teleconsultation/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const freshData = response.data;
      if (freshData?.teleconsultation_start_time) {
        console.log('✅ API confirms doctor accepted, redirecting to chat...');
        navigate(`/chat/${teleconsultationId}/${token}`);
        return true;
      } else {
        console.log('⚠️ API says doctor has NOT accepted yet, staying on page');
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to verify teleconsultation status:', error);
      return false;
    }
  }, [teleconsultationId, decryptedToken, token, navigate]);

  // ZIM setup functions
  const setupConversationListener = useCallback((targetUserId: string) => {
    console.log('🎧 Menyiapkan listener ZIM untuk user:', targetUserId);

    // Clear existing listeners
    zim.off('messageReceived');
    zim.off('conversationChanged');
    zim.off('peerMessageReceived');

    zim.on(
      'peerMessageReceived',
      async (
        zim: any,
        { messageList, fromConversationID }: { messageList: ZIMMessage[]; fromConversationID: string }
      ) => {
        console.log('peerMessageReceived', messageList, fromConversationID);

        // Filter for recent accepted messages (within last 5 minutes)
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        const acceptedMessages = messageList.filter(
          (msg: any) => {
            const isAccepted = typeof msg.message === 'string' &&
              msg.message.startsWith('accepted') &&
              msg.message.includes(teleconsultationId || '');
            const isRecent = msg.timestamp ? msg.timestamp > fiveMinutesAgo : true;
            return isAccepted && isRecent;
          }
        );

        if (acceptedMessages.length > 0 && teleconsultationId) {
          console.log('✅ Pesan konfirmasi ditemukan! Verifying with API...');
          await verifyAndRedirect();
        }
      }
    );

    // Message received listener
    zim.on('messageReceived', async (zimInstance: any, messageList: any[]) => {
      console.log('📨 Pesan baru diterima:', messageList);
      console.log('Teleconsultation ID:', teleconsultationId);

      // Filter for recent accepted messages (within last 5 minutes)
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      const acceptedMessages = messageList.filter(
        (msg: any) => {
          const isAccepted = typeof msg.message === 'string' &&
            msg.message.startsWith('accepted') &&
            msg.message.includes(teleconsultationId || '');
          const isRecent = msg.timestamp ? msg.timestamp > fiveMinutesAgo : true;
          return isAccepted && isRecent;
        }
      );

      if (acceptedMessages.length > 0 && teleconsultationId) {
        console.log('✅ Pesan konfirmasi ditemukan! Verifying with API...');
        await verifyAndRedirect();
      }
    });

    // Conversation changed listener
    zim.on('conversationChanged', async (conversationList: any) => {
      console.log('💬 Percakapan berubah:', conversationList);
      const config = { nextMessage: 0, count: 30, reverse: true };
      try {
        const chatHistory = await zim.queryHistoryMessage(targetUserId, 0, config);

        // Filter for recent accepted messages (within last 5 minutes)
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        const recentAcceptedMessages = chatHistory.messageList.filter(
          (msg: any) => {
            const isAccepted = typeof msg.message === 'string' && msg.message.startsWith('accepted');
            const matchesId = msg.message?.split('|')[1]?.trim() === teleconsultationId;
            const isRecent = msg.timestamp ? msg.timestamp > fiveMinutesAgo : false;
            return isAccepted && matchesId && isRecent;
          }
        );

        if (recentAcceptedMessages.length > 0) {
          console.log('✅ Recent accepted message found, verifying with API...');
          await verifyAndRedirect();
        }
      } catch (err) {
        console.error('❌ Gagal ambil riwayat pesan:', err);
      }
    });
  }, [teleconsultationId, verifyAndRedirect]);


  const loginAndSetupZIM = useCallback(async () => {
    console.log('loginAndSetupZIM called');
    setZimLoginFailed(false);

    if (!teleconsultationData || !process.env.REACT_APP_ZEGOCLOUD_APP_ENV) {
      console.log('⚠️ Teleconsultation data atau environment variable tidak tersedia');
      setZimLoginFailed(true);
      return;
    }

    if (!isValidTeleconsultationData(teleconsultationData)) {
      console.log('⚠️ Teleconsultation data tidak lengkap, skip login');
      setZimLoginFailed(true);
      return;
    }

    try {
      const envSuffix = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || '';
      const userId = `LMS${teleconsultationData.patient_id}${envSuffix}`;
      const username = teleconsultationData.patient_name;
      const practitionerId = `LMS${teleconsultationData.practitioner_id}${envSuffix}`;

      console.log('🔑 Login ZIM:', { userId, username, practitionerId });

      const loginSuccess = await loginToZego(userId, username);
      console.log('✅ ZIM login result:', loginSuccess);

      if (loginSuccess) {
        console.log('🎯 Login berhasil, menyiapkan listener...');
        setupConversationListener(practitionerId);
        previousUserDokterRef.current = practitionerId;
        console.log('🎯 Login dan listener berhasil di-setup');
      } else {
        console.error('❌ ZIM login returned false');
        setZimLoginFailed(true);
      }
    } catch (error) {
      console.error('❌ Login ZIM gagal:', error);
      setZimLoginFailed(true);
    }
  }, [teleconsultationData, setupConversationListener]);

  // Load initial data - ALWAYS fetch fresh teleconsultation data to prevent stale redirects
  const loadInitialData = useCallback(async () => {
    // Guard against duplicate calls (React StrictMode in dev)
    if (isLoadingRef.current) {
      console.log('⏭️ Skipping duplicate loadInitialData call');
      return;
    }
    isLoadingRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      // Try to get user data from localStorage (this is safe to cache)
      const savedUserData = localStorage.getItem('userData');
      let userDataValid = false;

      if (savedUserData) {
        try {
          const parsedUserData = JSON.parse(savedUserData);
          if (isValidUserData(parsedUserData)) {
            console.log('✅ UserData dari localStorage valid');
            setUserData(parsedUserData);
            userDataValid = true;
          }
        } catch (e) {
          console.warn('⚠️ UserData dari localStorage corrupt, akan fetch ulang');
          localStorage.removeItem('userData');
        }
      }

      // IMPORTANT: Always clear and fetch fresh teleconsultation data
      // This prevents premature redirect from stale teleconsultation_start_time
      console.log('🔄 Clearing stale teleconsultation data and fetching fresh...');
      localStorage.removeItem('teleconsultationData');

      // Fetch data from API
      const promises: Promise<UserData | TeleconsultationData | null>[] = [];
      if (!userDataValid) promises.push(fetchUserData());
      promises.push(fetchTeleconsultationData()); // ALWAYS fetch fresh

      const results = await Promise.all(promises);

      let fetchIndex = 0;
      if (!userDataValid) {
        if (results[fetchIndex]) {
          setUserData(results[fetchIndex] as UserData);
        }
        fetchIndex++;
      }

      if (results[fetchIndex]) {
        setTeleconsultationData(results[fetchIndex] as TeleconsultationData);
      }

      setIsDataLoaded(true);
    } catch (error: any) {
      console.error('❌ Error loading initial data:', error);
      setError('Terjadi kesalahan saat memuat data. Silakan coba lagi.');
      setIsDataLoaded(true);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [fetchUserData, fetchTeleconsultationData]);

  // Effects
  useEffect(() => {
    loadInitialData();

    return () => {
      isCancelledRef.current = true;
    };
  }, [loadInitialData]);

  // ✅ Polling: Check API every 10 seconds to see if doctor accepted
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingActiveRef = useRef(false); // Guard against duplicate polling from StrictMode

  useEffect(() => {
    if (!teleconsultationId || !decryptedToken) {
      return;
    }

    // Prevent duplicate polling from React StrictMode
    if (pollingIntervalRef.current || isPollingActiveRef.current) {
      console.log('⏭️ Polling already active, skipping...');
      return;
    }

    isPollingActiveRef.current = true;

    const checkStatus = async () => {
      console.log('🔍 Polling API for teleconsultation status...');
      try {
        const response = await axios.get(`teleconsultation/${teleconsultationId}`, {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        });

        const freshData = response.data;

        if (freshData?.teleconsultation_start_time) {
          console.log('✅ Polling: Doctor accepted! Redirecting to chat...');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          navigate(`/chat/${teleconsultationId}/${token}`);
        } else {
          console.log('⏳ Polling: Doctor has not accepted yet');
        }
      } catch (err) {
        console.error('❌ Polling API error:', err);
      }
    };

    // Polling starts after 10 seconds (loadInitialData already fetches on mount)
    console.log('🔄 Starting API polling (first poll in 10s)...');
    pollingIntervalRef.current = setInterval(checkStatus, 10 * 1000);

    return () => {
      console.log('🛑 Stopping API polling');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingActiveRef.current = false;
    };
  }, [teleconsultationId, decryptedToken, navigate, token]);

  useEffect(() => {
    if (!isDataLoaded || !teleconsultationData) return;

    // 1️⃣ Cek dulu kalau konsultasi sudah dimulai
    if (teleconsultationData.teleconsultation_start_time) {
      console.log("🚀 Dokter sudah accept — langsung masuk chat");
      navigate(`/chat/${teleconsultationId}/${token}`);
      return; // stop, tidak perlu login ZIM
    }

    // 2️⃣ Kalau belum mulai → pasang listener ZIM
    console.log('🔄 Data loaded, memulai login & setup ZIM...');
    loginAndSetupZIM();

  }, [isDataLoaded, teleconsultationData, loginAndSetupZIM]);


  // Timer fallback effect
  // useEffect(() => {
  //   if (timeLeft <= 0 && teleconsultationId) {
  //     console.log('⏰ Timer habis, redirect ke chat fallback');
  //     navigate(`/chat/${teleconsultationId}/${token}`);
  //     return;
  //   }

  //   const timer = setInterval(() => {
  //     setTimeLeft((prev) => prev - 1);
  //   }, 1000);

  //   return () => clearInterval(timer);
  // }, [timeLeft, navigate, teleconsultationId]);

  // NOTE: Second timer useEffect removed - consolidated into the first timer useEffect (lines 52-105)
  // This prevents race conditions between duplicate timer logic

  // Handler tombol
  const handleRetry = () => {
    // setTimeLeft(210); // reset timer
    setIsTimeout(false);
    // loginAndSetupZIM(); // coba login ulang
    createConsultation();
  };

  const handleBack = () => {
    sendMessageToReactNativeApp({ action: 'goBack' });
  };

  // Create consultation with proper error handling
  const createConsultation = useCallback(async () => {
    if (!teleconsultationId || !decryptedToken) {
      setError('Data tidak lengkap untuk membuat konsultasi.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const doctorResponse = await axios.get(`user/doctor-code/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const doctorCode = doctorResponse.data;

      if (!doctorCode) {
        setError('Kode dokter tidak ditemukan.');
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        "user/consultation",
        {
          doctorId: doctorCode,
          mediaType: "chat",
        },
        {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
          },
        }
      );

      if (response.data?.code === 200) {
        const roomUrl = response.data.data?.roomUrl;
        if (roomUrl) {
          // Reset timer for new consultation
          localStorage.removeItem(`timer_start_${teleconsultationId}`);
          window.location.href = roomUrl;
        } else {
          setError('Room URL tidak ditemukan. Silakan coba lagi.');
        }
      } else {
        setError(response.data?.message || 'Gagal membuat konsultasi. Silakan coba lagi.');
      }
    } catch (error: any) {
      console.error("Error saat createConsultation:", error);
      if (isAxiosError(error) && error.response?.status === 403) {
        navigate('/forbidden');
      } else {
        setError('Terjadi kesalahan saat menghubungi server. Silakan coba lagi.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [teleconsultationId, decryptedToken, navigate]);



  // Error modal component for reuse
  const ErrorModal = ({ title, message, onRetry, onBack }: {
    title: string;
    message: string;
    onRetry?: () => void;
    onBack: () => void;
  }) => (
    <div className="absolute inset-0 bg-black/40 flex items-end z-50">
      <div className="bg-white rounded-t-2xl w-full p-6 relative">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-lg"
          onClick={onBack}
        >
          ✕
        </button>
        <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto">
          <span className="text-white text-4xl">✖</span>
        </div>
        <h2 className="mt-4 text-lg font-bold text-center text-black">
          {title}
        </h2>
        <p className="text-sm text-gray-500 text-center mt-1">
          {message}
        </p>
        <div className="flex gap-4 mt-6">
          <button
            onClick={onBack}
            className="flex-1 py-2 rounded-full border border-red-500 text-red-500"
          >
            Kembali
          </button>
          {onRetry && (
            <button
              onClick={onRetry}
              className="flex-1 py-2 rounded-full bg-red-500 text-white disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Memuat...' : 'Coba Lagi'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Show error if token is invalid
  if (!isTokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#fff5f5] to-white flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto">
            <span className="text-white text-4xl">✖</span>
          </div>
          <h2 className="mt-4 text-lg font-bold text-black">Token Tidak Valid</h2>
          <p className="text-sm text-gray-500 mt-1">Sesi Anda tidak valid atau sudah berakhir.</p>
          <button
            onClick={handleBack}
            className="mt-6 px-6 py-2 rounded-full border border-red-500 text-red-500"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fff5f5] to-white flex flex-col items-center justify-between">
      <div className="flex flex-col items-center justify-center flex-grow mt-4 px-4 text-center">
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Loading state during initial data fetch */}
          {isLoading && !isDataLoaded && (
            <div className="flex flex-col items-center gap-4">
              <img
                src="/logo-manulife.png"
                alt="Loading"
                className="w-40 h-40 animate-pulse"
              />
              {timeLeft !== null && (
                <p className="text-2xl font-semibold text-black">
                  {formatTime(timeLeft)}
                </p>
              )}


              <p className="text font-bold text-xl">
                Menghubungkan Anda ke dokter...
              </p>
              <p className="text text-sm">
                Dokter akan terhubung ke Anda sebentar lagi
              </p>
            </div>
          )}

          {/* Error state */}
          {error && !isTimeout && (
            <ErrorModal
              title="Terjadi Kesalahan"
              message={error}
              onRetry={() => {
                setError(null);
                loadInitialData();
              }}
              onBack={handleBack}
            />
          )}

          {/* ZIM Login Failed state */}
          {zimLoginFailed && !error && !isTimeout && isDataLoaded && (
            <ErrorModal
              title="Gagal Terhubung"
              message="Gagal menghubungkan ke layanan chat. Silakan coba lagi."
              onRetry={() => {
                setZimLoginFailed(false);
                loginAndSetupZIM();
              }}
              onBack={handleBack}
            />
          )}

          {/* Normal connecting state */}
          {!isTimeout && !error && !zimLoginFailed && isDataLoaded && !isLoading && (
            <div className="flex flex-col items-center gap-4">
              <img src="/logo-manulife.png" alt="Loading" className="w-40 h-40 animate-pulse" />
              {timeLeft !== null && (
                <p className="text-2xl font-semibold text-black">
                  {formatTime(timeLeft)}
                </p>
              )}
              <p className="text font-bold text-xl">Menghubungkan Anda ke dokter...</p>
              <p className="text text-sm">Dokter akan terhubung ke Anda sebentar lagi</p>
            </div>
          )}

          {/* Timeout state */}
          {isTimeout && (
            <ErrorModal
              title="Gagal Menghubungkan"
              message="Saat ini kami gagal menghubungkan Anda ke dokter. Silakan coba lagi."
              onRetry={handleRetry}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectingDoctor;
